/**
 * Branch context — the "active restaurant" the operator is currently working at.
 *
 * Rules (KFC/McDonald's pattern):
 *  - **Only the OWNER** can switch branches or view the special "All branches"
 *    aggregate (id === '__all__').
 *  - Every other staff role (admin, kitchen, cashier, waiter, driver) is
 *    **LOCKED to the branch their account is assigned to**. They cannot see
 *    or interact with any other branch — the switcher is hidden and any
 *    stale localStorage value is overwritten on next render.
 *  - The currently-active branch id is persisted in localStorage so reloads
 *    keep the owner at the same store. For locked roles this just mirrors
 *    `user.branch_id`.
 *
 * Pages that are branch-scoped should call `useBranchScope()` to get a
 * `branch_id` they can attach to their API params. When the active branch
 * is "All branches" (owner-only), that helper returns null (so APIs see
 * no filter).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { useAuth } from './auth';

const STORAGE_KEY = 'cg_active_branch';
export const ALL_BRANCHES = '__all__';

// Only owners may roam between branches; every other role is pinned to
// their `branch_id`. Even admin is locked now — admins manage just their
// own store under the new ops governance rules.
const SWITCHER_ROLES = new Set(['owner']);
const LOCKED_ROLES = new Set(['admin', 'kitchen', 'cashier', 'waiter', 'driver']);

const BranchContext = createContext(null);

export function BranchProvider({ children }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(true);

  // Load list of branches (public endpoint) on mount and whenever the user
  // changes (so owners see the freshest list after creating a new branch).
  const reload = useCallback(async () => {
    try {
      const { data } = await api.get('/branches');
      setBranches(Array.isArray(data) ? data : []);
    } catch (e) {
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Whenever user / branch list changes, reconcile the active branch:
  //  • Locked roles (admin/kitchen/cashier/waiter/driver) → forced to
  //    user.branch_id (or first branch as fallback).
  //  • Owners without saved selection → first branch (not "All branches"
  //    by default — they must opt-in explicitly via the switcher).
  //  • Owners with stale id (branch was deleted) → first branch.
  useEffect(() => {
    if (!user || !branches.length) return;
    const role = user.role;
    const valid = (id) => id === ALL_BRANCHES || branches.some((b) => b.id === id);

    if (LOCKED_ROLES.has(role)) {
      const forced = user.branch_id && branches.some((b) => b.id === user.branch_id)
        ? user.branch_id
        : branches[0].id;
      if (forced !== activeId) {
        setActiveId(forced);
        localStorage.setItem(STORAGE_KEY, forced);
      }
      return;
    }
    if (!activeId || !valid(activeId)) {
      const fallback = user.branch_id && branches.some((b) => b.id === user.branch_id)
        ? user.branch_id
        : branches[0].id;
      setActiveId(fallback);
      localStorage.setItem(STORAGE_KEY, fallback);
    }
  }, [user, branches, activeId]);

  const setActive = useCallback((id) => {
    if (!user) return;
    // Locked roles can never change branches — silently ignore. Backend
    // also re-checks any branch_id passed in API requests for non-owners.
    if (!SWITCHER_ROLES.has(user.role)) return;
    setActiveId(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }, [user]);

  const canSwitch = !!user && SWITCHER_ROLES.has(user.role);
  const canSelectAll = canSwitch;
  const isAllBranches = activeId === ALL_BRANCHES;
  const activeBranch = isAllBranches
    ? null
    : branches.find((b) => b.id === activeId) || null;

  // The branch_id query param that scoped API calls should use.
  // Returns null when "All branches" is selected.
  const scopeBranchId = isAllBranches ? null : (activeBranch?.id || null);

  const value = useMemo(() => ({
    branches,
    activeId,
    activeBranch,
    isAllBranches,
    canSwitch,
    canSelectAll,
    loading,
    scopeBranchId,
    setActive,
    reload,
  }), [branches, activeId, activeBranch, isAllBranches, canSwitch, canSelectAll, loading, scopeBranchId, setActive, reload]);

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch() {
  const ctx = useContext(BranchContext);
  if (!ctx) {
    // Fallback no-op when used outside the provider (e.g. customer pages)
    return {
      branches: [], activeId: null, activeBranch: null, isAllBranches: false,
      canSwitch: false, canSelectAll: false, loading: false, scopeBranchId: null,
      setActive: () => {}, reload: () => {},
    };
  }
  return ctx;
}

/** Convenience: returns the branch_id (or null) to pass to scoped APIs. */
export function useBranchScope() {
  return useBranch().scopeBranchId;
}
